import { createFeatureSelector, createSelector } from '@ngrx/store';

import { CartFeatures, CartOfferLine, CartSellerLine, CartState, ShoppingCartData, StoreCart } from '../models';
import { SHOPPING_CART_FEATURE_ID } from '../constants/shopping-cart.constants';
import { ITotal } from '~shared/modules/cart/models/total.model';
import { OFFER_BUSINESS_MODEL_B2B_ONLY } from '~shared/constants';

const selectShoppingCartState = createFeatureSelector<CartState>(SHOPPING_CART_FEATURE_ID);

export const selectLoadingStatus = createSelector(selectShoppingCartState, (state: CartState) => state.loading);

export const selectLoadingWithDiscountStatus = createSelector(selectShoppingCartState, (state: CartState) => state.loading || state.payload?.discountCode?.loading === true);

export const selectCurrency = createSelector(
  selectShoppingCartState,
  (state: CartState): string => state.payload.currency
);

const selectPayload = createSelector(selectShoppingCartState, (state: CartState): StoreCart => state.payload);

export const selectSellerLines = createSelector(
  selectShoppingCartState,
  (state: CartState): CartSellerLine[] => state.payload.sellers
);

export const selectOfferLines = () =>
  createSelector(selectPayload, (state, props): CartOfferLine[] =>
    state.offers.filter((offerLine: CartOfferLine) => props.offers.includes(offerLine.offer))
  );

export const selectOffersHaveValueAddedServices = () =>
  createSelector(selectOfferLines(), (cartOfferLines, props): boolean =>
    cartOfferLines.find(line => line.valueAddedServices?.length > 0) !== undefined
  );

export const selectSellerTotals = () =>
  createSelector(selectPayload, (state, props): CartSellerLine[] =>
    state.sellers.filter((sellerLine: CartSellerLine) => props.sellers.includes(sellerLine.seller.id))
  );

export const selectOffers = createSelector(selectPayload, (state: StoreCart): CartOfferLine[] => state.offers);

export const selectCartFeatures = createSelector(selectPayload, (state: StoreCart): CartFeatures => state.features);

export const selectShoppingCartData = createSelector(selectPayload, (state: StoreCart): ShoppingCartData => {
  const offers = state.offers;
  const totals = state.totals;
  const currency = state.currency;
  return { offers, totals, currency };
});

export const selectTotals = createSelector(selectPayload, (state: StoreCart): ITotal => state.totals);

export const selectHasInvalidOffer = createSelector(selectOffers, (state: CartOfferLine[]): boolean =>
  state.some((offerLine: CartOfferLine) => !!offerLine.error && !!!offerLine.error.details['offer_model'])
);

export const selectDiscountCode = createSelector(selectPayload, (state: StoreCart) => {
  const applied = state.discountCode?.applied;
  const code = state.discountCode?.code;
  const value = state.discountCode?.value;
  const error = state.discountCode?.error;
  const loading = state.discountCode?.loading;
  return { applied, value, error, code, loading };
});

export const hasErrors = createSelector(selectShoppingCartState, (state: CartState): boolean => !!state.error);

export const hasDiscountCode = createSelector(
  selectPayload,
  (state: StoreCart): boolean => state.discountCode?.applied
);

export const selectHasB2BOffer = createSelector(selectOffers, (state: CartOfferLine[]): boolean =>
  state.some((offerLine: CartOfferLine) => !!offerLine.error && !!offerLine.error.details['offer_model'])
);

export const selectHasB2BOfferForCart = createSelector(selectOffers, (state: CartOfferLine[]): boolean =>
  state.some((offerLine: CartOfferLine) => offerLine.offerBusinessModel === OFFER_BUSINESS_MODEL_B2B_ONLY)
);

export const selectHasFreightForwarding = createSelector(selectOffers, (state: CartOfferLine[]): boolean =>
  state.some((offerLine: CartOfferLine) => offerLine.item.hasFreightForwarding)
);

export const selectCanCheckout = createSelector(selectShoppingCartState, (state: CartState): boolean => {

  if (state.error) {
    // There's an error while loading the cart
    return false;
  }

  if (!state.payload.offers.length) {
    // The cart is empty
    return false;
  }

  if (state.payload.offers.some((offerLine: CartOfferLine) => offerLine.deleting)) {
    // At least one offer is updating or deleting
    return false;
  }

  // At least one offer should be valid (without error)
  return !state.payload.offers.every((offerLine: CartOfferLine) => !!offerLine.error);
});

export const selectIsLoading = createSelector(selectShoppingCartState, (state: CartState): boolean => {
  if (state.loading) {
    return true;
  }

  if (state.payload.offers.some((offerLine: CartOfferLine) => offerLine.loading)) {
    return true;
  }

  return false;
});

export const selectOrderContainsAlcohol = createSelector(selectShoppingCartState, (state: CartState): boolean => {
  return !!state.payload.hasAlcohol;
});

export const selectHasCrossBorder = createSelector(
  selectShoppingCartState,
  (state: CartState): boolean => {
    if (state.payload.features) {
      return !!state?.payload?.features?.isCrossBorder
    }
    return false
  }
);

